import { inject, Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import {
  Client,
  FileDataResponse,
  FileResponse,
  IFileResponse,
  IWorldStreamResource,
  StreamsCollection,
  StreamExtendedResource,
  UserMinimalResource,
  VideoResource,
  WorldStreamCollection,
} from '@api/client';
import {
  FILE_COUNT_ON_PROFILE_PAGE,
  PROFILE_FILES_PAGES_FOR_PLAYLIST,
  PROFILE_STREAMS_PAGES_FOR_PLAYLIST,
  STREAM_COUNT_ON_PROFILE_PAGE,
} from '@constants/app.constants';
import { AuthService, ParsedUser } from '@services/auth.service';
import { SoundUnlockService } from '@avatar/services/sound-unlock.service';
import { VplayProfileSource, VplayProfileStreamsService } from '@avatar/services/vplay-profile-streams.service';
import { buildProfileMediaPlaylist } from '@utils/profile-media-streams';
import { isFileMedia } from '@utils/file-media.utils';

export interface ProfileMediaNavigationOptions {
  userId?: number | null;
  parsedUser?: ParsedUser | null;
  backHref?: string | null;
  source?: VplayProfileSource;
  unlockSound?: boolean;
}

export type ProfileMediaOpenFailureReason = 'not-found' | 'unsupported' | 'failed';

export interface ProfileMediaOpenResult {
  opened: boolean;
  reason?: ProfileMediaOpenFailureReason;
  contentType?: ProfileMediaUrlTargetType;
  contentId?: number | null;
  fileId?: number | null;
  userId?: number | null;
}

type ProfileMediaUrlTargetType = 'file' | 'stream';

interface ProfileMediaUrlTarget {
  type: ProfileMediaUrlTargetType;
  contentId: number;
  userId: number | null;
}

interface ProfileFileLoadResult {
  file: FileResponse | null;
  notFound: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileMediaNavigationService {
  private readonly client = inject(Client);
  private readonly navController = inject(NavController);
  private readonly authService = inject(AuthService);
  private readonly soundUnlockService = inject(SoundUnlockService);
  private readonly vplayProfileStreamsService = inject(VplayProfileStreamsService);

  public async openFileById(fileId: number | string | null | undefined, options: ProfileMediaNavigationOptions = {}): Promise<boolean> {
    const result: ProfileMediaOpenResult = await this.openFileByIdWithResult(fileId, options);
    return result.opened;
  }

  public async openFileByIdWithResult(
    fileId: number | string | null | undefined,
    options: ProfileMediaNavigationOptions = {}
  ): Promise<ProfileMediaOpenResult> {
    const normalizedFileId: number | null = this.parsePositiveInt(fileId);
    if (normalizedFileId == null) {
      return { opened: false, reason: 'failed', contentType: 'file' };
    }

    const fileLoadResult: ProfileFileLoadResult = await this.loadFile(normalizedFileId);
    const file: FileResponse | null = fileLoadResult.file;
    const userId: number | null = file
      ? this.resolveFileUserId(file, options.userId)
      : this.parsePositiveInt(options.userId);
    if (userId == null) {
      return {
        opened: false,
        reason: fileLoadResult.notFound ? 'not-found' : 'failed',
        contentType: 'file',
        contentId: normalizedFileId,
        fileId: normalizedFileId,
        userId,
      };
    }

    const files: IFileResponse[] = await this.loadProfileFiles(userId, normalizedFileId, file);
    const clickedFile: IFileResponse | null = file ?? this.findFileById(files, normalizedFileId);
    if (!clickedFile) {
      return {
        opened: false,
        reason: fileLoadResult.notFound ? 'not-found' : 'failed',
        contentType: 'file',
        contentId: normalizedFileId,
        fileId: normalizedFileId,
        userId,
      };
    }

    if (!isFileMedia(clickedFile)) {
      return {
        opened: false,
        reason: 'unsupported',
        contentType: 'file',
        contentId: normalizedFileId,
        fileId: normalizedFileId,
        userId,
      };
    }

    const opened: boolean = await this.openFileFromList(files, clickedFile, {
      ...options,
      userId,
    });

    return opened
      ? { opened: true, contentType: 'file', contentId: normalizedFileId, fileId: normalizedFileId, userId }
      : {
        opened: false,
        reason: fileLoadResult.notFound ? 'not-found' : 'failed',
        contentType: 'file',
        contentId: normalizedFileId,
        fileId: normalizedFileId,
        userId,
      };
  }

  public async openFileFromList(
    files: IFileResponse[] | null | undefined,
    clickedFile: IFileResponse | null | undefined,
    options: ProfileMediaNavigationOptions = {}
  ): Promise<boolean> {
    if (!files?.length || !clickedFile) {
      return false;
    }

    const userId: number | null = this.resolveFileUserId(clickedFile, options.userId);
    if (userId == null) {
      return false;
    }

    const parsedUser: ParsedUser | null = this.resolveParsedUser(userId, options.parsedUser);
    const playlist = buildProfileMediaPlaylist(files, clickedFile, parsedUser);
    if (!playlist.streams.length || playlist.targetFileId == null) {
      return false;
    }

    this.vplayProfileStreamsService.setCustomStreams(playlist.streams, {
      backHref: this.resolveBackHref(userId, options.backHref),
      source: options.source,
      initialTargetId: playlist.targetFileId,
    });

    if (options.unlockSound === true) {
      await this.soundUnlockService.unlock();
    }

    await this.navController.navigateForward(['/avatar', 'vplay', userId, playlist.targetFileId]);
    return true;
  }

  public async openStreamById(videoId: number | string | null | undefined, options: ProfileMediaNavigationOptions = {}): Promise<boolean> {
    const normalizedVideoId: number | null = this.parsePositiveInt(videoId);
    if (normalizedVideoId == null) {
      return false;
    }

    const stream: StreamExtendedResource | null = await this.loadStream(normalizedVideoId);
    let userId: number | null = this.resolveStreamUserId(stream, options.userId);
    let worldStream: IWorldStreamResource | null = null;
    if (userId == null) {
      worldStream = await this.loadWorldStream(normalizedVideoId);
      userId = this.resolveWorldStreamUserId(worldStream, options.userId);
    }
    if (userId == null) {
      return false;
    }

    const streams: StreamExtendedResource[] = await this.loadProfileStreams(userId, normalizedVideoId, stream);
    const clickedStream: StreamExtendedResource | null = this.findStreamByVideoId(streams, normalizedVideoId)
      ?? stream
      ?? this.createStreamFromWorldStream(worldStream, normalizedVideoId);
    if (!clickedStream) {
      return false;
    }

    return this.openStreamFromList(streams, clickedStream, {
      ...options,
      userId,
    });
  }

  public async openStreamFromList(
    streams: StreamExtendedResource[] | null | undefined,
    clickedStream: StreamExtendedResource | null | undefined,
    options: ProfileMediaNavigationOptions = {}
  ): Promise<boolean> {
    if (!clickedStream) {
      return false;
    }

    const targetVideoId: number | null = this.resolveStreamVideoId(clickedStream);
    if (targetVideoId == null) {
      return false;
    }

    const userId: number | null = this.resolveStreamUserId(clickedStream, options.userId);
    if (userId == null) {
      return false;
    }

    const sourceStreams: StreamExtendedResource[] = streams?.length ? streams : [clickedStream];
    this.vplayProfileStreamsService.setProfileStreams(
      this.ensureStreamInList(sourceStreams, clickedStream, targetVideoId),
      { backHref: this.resolveBackHref(userId, options.backHref) }
    );

    if (options.unlockSound === true) {
      await this.soundUnlockService.unlock();
    }

    await this.navController.navigateForward(['/avatar', 'vplay', targetVideoId]);
    return true;
  }

  public async openFromProfileMediaUrl(encodedUrl: string | null | undefined, options: ProfileMediaNavigationOptions = {}): Promise<boolean> {
    const result: ProfileMediaOpenResult = await this.openFromProfileMediaUrlWithResult(encodedUrl, options);
    return result.opened;
  }

  public async openFromProfileMediaUrlWithResult(
    encodedUrl: string | null | undefined,
    options: ProfileMediaNavigationOptions = {}
  ): Promise<ProfileMediaOpenResult> {
    if (!encodedUrl) {
      return { opened: false, reason: 'failed' };
    }

    const target: ProfileMediaUrlTarget | null = this.resolveProfileMediaUrlTarget(encodedUrl);
    if (!target) {
      return { opened: false, reason: 'failed' };
    }

    if (target.type === 'stream') {
      const opened: boolean = await this.openStreamById(target.contentId, {
        ...options,
        userId: target.userId ?? options.userId ?? null,
      });
      return {
        opened,
        reason: opened ? undefined : 'failed',
        contentType: 'stream',
        contentId: target.contentId,
        userId: target.userId,
      };
    }

    const result: ProfileMediaOpenResult = await this.openFileByIdWithResult(target.contentId, {
      ...options,
      userId: target.userId ?? options.userId ?? null,
    });

    return {
      opened: result.opened,
      reason: result.reason,
      contentType: 'file',
      contentId: result.contentId ?? target.contentId,
      fileId: result.fileId ?? target.contentId,
      userId: result.userId ?? target.userId,
    };
  }

  public isProfileFileUrl(encodedUrl: string | null | undefined): boolean {
    if (!encodedUrl) {
      return false;
    }

    const target: ProfileMediaUrlTarget | null = this.resolveProfileMediaUrlTarget(encodedUrl);
    return target?.type === 'file';
  }

  private async loadFile(fileId: number): Promise<ProfileFileLoadResult> {
    try {
      const response: FileDataResponse = await firstValueFrom(this.client.fileGetFile(fileId));
      return {
        file: response?.data ?? null,
        notFound: false,
      };
    } catch (error) {
      if (this.isNotFoundError(error)) {
        console.warn('Profile media file is not available', { fileId });
        return {
          file: null,
          notFound: true,
        };
      }

      console.error('Error loading profile media file', error);
      return {
        file: null,
        notFound: false,
      };
    }
  }

  private async loadStream(videoId: number): Promise<StreamExtendedResource | null> {
    try {
      return await firstValueFrom(this.client.videosGet(videoId));
    } catch (error) {
      console.error('Error loading profile stream', error);
      return null;
    }
  }

  private async loadWorldStream(videoId: number): Promise<IWorldStreamResource | null> {
    try {
      const response: WorldStreamCollection = await firstValueFrom(
        this.client.videosWorldIndex(undefined, undefined, 1, 0, videoId, [])
      );
      return response?.data?.[0] ?? null;
    } catch (error) {
      console.error('Error loading profile world stream', error);
      return null;
    }
  }

  private async loadProfileFiles(
    userId: number,
    targetFileId: number,
    fallbackFile: IFileResponse | null
  ): Promise<IFileResponse[]> {
    const collected: IFileResponse[] = [];
    const seenIds = new Set<number>();
    let foundTarget = false;

    for (let page = 1; page <= PROFILE_FILES_PAGES_FOR_PLAYLIST; page += 1) {
      try {
        const response = await firstValueFrom(
          this.client.profileFileIndex('all', userId, 'created_at', 'desc', FILE_COUNT_ON_PROFILE_PAGE, page)
        );
        const pageFiles: FileResponse[] = response?.data ?? [];
        if (pageFiles.length === 0) {
          break;
        }

        for (const file of pageFiles) {
          const idValue: number | null = this.parsePositiveInt(file?.id);
          if (idValue == null || seenIds.has(idValue)) {
            continue;
          }

          seenIds.add(idValue);
          collected.push(file);
          if (idValue === targetFileId) {
            foundTarget = true;
          }
        }

        if (foundTarget || pageFiles.length < FILE_COUNT_ON_PROFILE_PAGE) {
          break;
        }
      } catch (error) {
        console.error('Error loading profile media playlist', error);
        break;
      }
    }

    if (!foundTarget) {
      const fallbackId: number | null = fallbackFile ? this.parsePositiveInt(fallbackFile.id) : null;
      if (fallbackFile && fallbackId != null && !seenIds.has(fallbackId)) {
        collected.push(fallbackFile);
      }
    }

    return collected;
  }

  private async loadProfileStreams(
    userId: number,
    targetVideoId: number,
    fallbackStream: StreamExtendedResource | null
  ): Promise<StreamExtendedResource[]> {
    const collected: StreamExtendedResource[] = [];
    const seenIds = new Set<number>();
    let foundTarget = false;

    for (let page = 0; page < PROFILE_STREAMS_PAGES_FOR_PLAYLIST; page += 1) {
      try {
        const response: StreamsCollection = await firstValueFrom(
          this.client.videosUser(
            userId,
            undefined,
            undefined,
            undefined,
            STREAM_COUNT_ON_PROFILE_PAGE,
            page * STREAM_COUNT_ON_PROFILE_PAGE,
            undefined,
            undefined,
            undefined,
            undefined,
          )
        );
        const pageStreams: StreamExtendedResource[] = response?.data ?? [];
        if (pageStreams.length === 0) {
          break;
        }

        for (const stream of pageStreams) {
          const idValue: number | null = this.resolveStreamVideoId(stream);
          if (idValue == null || seenIds.has(idValue)) {
            continue;
          }

          seenIds.add(idValue);
          collected.push(stream);
          if (idValue === targetVideoId) {
            foundTarget = true;
          }
        }

        if (foundTarget || pageStreams.length < STREAM_COUNT_ON_PROFILE_PAGE) {
          break;
        }
      } catch (error) {
        console.error('Error loading profile streams playlist', error);
        break;
      }
    }

    if (!foundTarget) {
      const fallbackId: number | null = fallbackStream ? this.resolveStreamVideoId(fallbackStream) : null;
      if (fallbackStream && fallbackId != null && !seenIds.has(fallbackId)) {
        collected.push(fallbackStream);
      }
    }

    return collected;
  }

  private resolveProfileMediaUrlTarget(encodedUrl: string): ProfileMediaUrlTarget | null {
    const url: URL | null = this.createUrl(encodedUrl);
    if (!url) {
      return null;
    }

    const pathSegments: string[] = url.pathname.split('/').filter((segment: string): boolean => segment.length > 0);
    const vplayTarget: ProfileMediaUrlTarget | null = this.resolveVplayUrlTarget(pathSegments);
    if (vplayTarget) {
      return vplayTarget;
    }

    const userId: number | null = this.resolveUserIdFromPathSegments(pathSegments);
    const fileIdFromPath: number | null = this.resolveFileIdFromPathSegments(pathSegments);
    if (fileIdFromPath != null) {
      return {
        type: 'file',
        contentId: fileIdFromPath,
        userId,
      };
    }

    const fileIdFromQuery: number | null = this.parsePositiveInt(url.searchParams.get('file_id'));
    if (fileIdFromQuery == null) {
      return null;
    }

    if (pathSegments.length === 1 && (pathSegments[0] === 'profile' || pathSegments[0].startsWith('@'))) {
      return {
        type: 'file',
        contentId: fileIdFromQuery,
        userId,
      };
    }

    if (pathSegments.length === 2 && pathSegments[0] === 'people' && userId != null) {
      return {
        type: 'file',
        contentId: fileIdFromQuery,
        userId,
      };
    }

    return null;
  }

  private resolveVplayUrlTarget(pathSegments: string[]): ProfileMediaUrlTarget | null {
    const routeIndex: number | null = this.resolveVplayRouteIndex(pathSegments);
    if (routeIndex == null) {
      return null;
    }

    const routeSegment: string | undefined = pathSegments[routeIndex];
    const segmentsAfterRoute: number = pathSegments.length - routeIndex - 1;
    const firstId: number | null = this.parsePositiveInt(pathSegments[routeIndex + 1]);
    if (firstId == null) {
      return null;
    }

    if (segmentsAfterRoute === 1) {
      return {
        type: 'stream',
        contentId: firstId,
        userId: null,
      };
    }

    if (segmentsAfterRoute !== 2 || routeSegment !== 'vplay') {
      return null;
    }

    const secondId: number | null = this.parsePositiveInt(pathSegments[routeIndex + 2]);
    if (secondId != null) {
      return {
        type: 'file',
        contentId: secondId,
        userId: firstId,
      };
    }

    return null;
  }

  private resolveVplayRouteIndex(pathSegments: string[]): number | null {
    const routeIndex: number = pathSegments.findIndex((segment: string, index: number): boolean => {
      if (segment !== 'vplay' && segment !== 'consumer') {
        return false;
      }
      return index === 0 || pathSegments[index - 1] === 'avatar';
    });

    return routeIndex === -1 ? null : routeIndex;
  }

  private createUrl(encodedUrl: string): URL | null {
    try {
      return new URL(this.decodeUrl(encodedUrl), this.getUrlOrigin());
    } catch (error) {
      console.error('Error parsing profile media URL', error);
      return null;
    }
  }

  private decodeUrl(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      console.warn('Error decoding profile media URL', error);
      return value;
    }
  }

  private getUrlOrigin(): string {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return 'https://app.pyjam.com';
  }

  private resolveUserIdFromPathSegments(pathSegments: string[]): number | null {
    if (pathSegments[0] === 'people') {
      return this.parsePositiveInt(pathSegments[1]);
    }
    if (pathSegments[0] === 'profile') {
      return this.authService.parsedUser?.id ?? null;
    }
    return null;
  }

  private resolveFileIdFromPathSegments(pathSegments: string[]): number | null {
    if (pathSegments.length === 4 && pathSegments[0] === 'people' && pathSegments[2] === 'file') {
      return this.parsePositiveInt(pathSegments[3]);
    }
    return null;
  }

  private resolveFileUserId(file: IFileResponse, fallbackUserId?: number | null): number | null {
    return this.parsePositiveInt(file.user_id) ?? this.parsePositiveInt(fallbackUserId);
  }

  private findFileById(files: IFileResponse[], fileId: number): IFileResponse | null {
    return files.find((file: IFileResponse): boolean => this.parsePositiveInt(file?.id) === fileId) ?? null;
  }

  private resolveStreamVideoId(stream: StreamExtendedResource | null | undefined): number | null {
    return this.parsePositiveInt(stream?.video_id) ?? this.parsePositiveInt(stream?.video?.id);
  }

  private resolveStreamUserId(stream: StreamExtendedResource | null | undefined, fallbackUserId?: number | null): number | null {
    return this.parsePositiveInt(stream?.user?.id)
      ?? this.parsePositiveInt(stream?.['author_id'])
      ?? this.parsePositiveInt(stream?.['user_id'])
      ?? this.parsePositiveInt(stream?.video?.['author_id'])
      ?? this.parsePositiveInt(stream?.video?.['user_id'])
      ?? this.parsePositiveInt(this.readNestedId(stream?.video?.['user']))
      ?? this.parsePositiveInt(fallbackUserId);
  }

  private resolveWorldStreamUserId(stream: IWorldStreamResource | null | undefined, fallbackUserId?: number | null): number | null {
    return this.parsePositiveInt(stream?.author_id)
      ?? this.parsePositiveInt(stream?.user?.['id'])
      ?? this.parsePositiveInt(stream?.['user_id'])
      ?? this.parsePositiveInt(fallbackUserId);
  }

  private resolveParsedUser(userId: number, parsedUser?: ParsedUser | null): ParsedUser | null {
    if (parsedUser !== undefined) {
      return parsedUser;
    }

    const currentUser: ParsedUser | null = this.authService.parsedUser;
    if (currentUser?.id === userId) {
      return currentUser;
    }
    return null;
  }

  private resolveBackHref(userId: number, backHref?: string | null): string {
    const normalizedBackHref: string = typeof backHref === 'string' ? backHref.trim() : '';
    if (normalizedBackHref.length > 0) {
      return normalizedBackHref;
    }

    const currentUserId: number | null = this.authService.parsedUser?.id ?? null;
    if (currentUserId === userId) {
      return '/profile';
    }
    return `/people/${userId}`;
  }

  private ensureStreamInList(
    streams: StreamExtendedResource[],
    targetStream: StreamExtendedResource,
    targetVideoId: number
  ): StreamExtendedResource[] {
    const hasTarget: boolean = streams.some((stream: StreamExtendedResource): boolean => this.resolveStreamVideoId(stream) === targetVideoId);
    return hasTarget ? streams : [...streams, targetStream];
  }

  private findStreamByVideoId(streams: StreamExtendedResource[], videoId: number): StreamExtendedResource | null {
    return streams.find((stream: StreamExtendedResource): boolean => this.resolveStreamVideoId(stream) === videoId) ?? null;
  }

  private createStreamFromWorldStream(
    worldStream: IWorldStreamResource | null | undefined,
    videoId: number
  ): StreamExtendedResource | null {
    if (!worldStream) {
      return null;
    }

    const user = new UserMinimalResource({
      id: this.resolveWorldStreamUserId(worldStream) ?? undefined,
      name: worldStream.user?.name,
      surname: worldStream.user?.surname,
      avatar: worldStream.user?.avatar,
      official: worldStream.user?.official,
    });
    const video = new VideoResource({
      id: worldStream.video_id ?? videoId,
      likes: worldStream.actions?.likes,
      dislikes: worldStream.actions?.dislikes,
      watches: worldStream.actions?.watches,
      comments_count: worldStream.actions?.comments_count,
    });
    const stream = new StreamExtendedResource({
      user,
      video,
      video_id: worldStream.video_id ?? videoId,
      live: worldStream.live,
      name: worldStream.name,
      description: worldStream.description,
      my_reaction: worldStream.my_actions?.my_reaction,
      i_watched: worldStream.my_actions?.i_watched,
      i_subscribed: worldStream.my_actions?.i_subscribed,
      comments_count: worldStream.actions?.comments_count,
    });
    stream['author_id'] = this.resolveWorldStreamUserId(worldStream);
    stream['live_viewers'] = worldStream.live_viewers;
    stream['live_ended'] = worldStream['live_ended'];
    return stream;
  }

  private readNestedId(value: unknown): unknown {
    if (value == null || typeof value !== 'object' || Array.isArray(value) || !('id' in value)) {
      return null;
    }

    return value.id;
  }

  private isNotFoundError(error: unknown): boolean {
    if (error == null || typeof error !== 'object') {
      return false;
    }

    if ('status' in error && error.status === 404) {
      return true;
    }

    return 'message' in error
      && typeof error.message === 'string'
      && error.message.toLowerCase().includes('not found');
  }

  private parsePositiveInt(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isSafeInteger(value) && value > 0 ? value : null;
    }

    if (typeof value !== 'string' || !/^\d+$/.test(value)) {
      return null;
    }

    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }
}
